import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material'
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import api from '@/services/api'

interface Document {
  id: string
  type: string
  title?: string
  createdOn: string
  status?: 'pending' | 'authorized' | 'rejected'
  downloadUrl?: string
}

interface DocumentsSectionProps {
  contractId: string // MongoDB contract ID
  documents?: Document[] // Optional: if provided, will use these instead of fetching
  loading?: boolean // Optional: if provided, will use this instead of internal loading
  onUpload?: () => void
}

interface ApiDocument {
  _id: string
  contract_id: string
  title: string
  type: string
  fileUrl: string
  fileKey: string
  notes_id?: string
  patientId?: string
  therapistId?: string
  isDeleted: boolean
  createdAt: string
  updatedAt: string
  __v?: number
}

interface DocumentsApiResponse {
  totalRecords: number
  totalPages: number
  currentPage: string
  limit: string
  data: ApiDocument[]
}

const DocumentsSection: React.FC<DocumentsSectionProps> = ({
  contractId,
  documents: externalDocuments,
  loading: externalLoading,
  onUpload,
}) => {
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(false)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // Fetch documents from API
  useEffect(() => {
    // If external documents are provided, use them instead of fetching
    // if (externalDocuments !== undefined) {
    //   setDocuments(externalDocuments)
    //   return
    // }

    // Fetch documents from API
    const fetchDocuments = async () => {
      if (!contractId) return

      setLoading(true)
      setError(null)
      try {
        const response = await api.get<DocumentsApiResponse>(
          `/contracts/${contractId}/documents-session`,
          {
            params: {
              page,
              limit,
            },
          }
        )

        const responseData = response.data

        // Map API response to Document interface
        const mappedDocuments: Document[] = (responseData.data || []).map(
          (doc: ApiDocument) => ({
            id: doc._id,
            type: doc.type,
            title: doc.title,
            createdOn: doc.createdAt,
            downloadUrl: doc.fileUrl,
            // Status is not in API response, so it will be undefined
          })
        )

        setDocuments(mappedDocuments)

        // Handle pagination info
        setTotalPages(responseData.totalPages || 1)
        setTotal(responseData.totalRecords || 0)
      } catch (err: any) {
        console.error('Failed to fetch documents:', err)
        setError(
          err.response?.data?.message ||
            'Failed to load documents. Please try again.'
        )
        setDocuments([])
      } finally {
        setLoading(false)
      }
    }

    fetchDocuments()
  }, [contractId, page, limit])

  // Use external loading state if provided, otherwise use internal
  const isLoading = externalLoading !== undefined ? externalLoading : loading

  const handleDownload = async (doc: Document) => {
    if (downloadingId || !doc.downloadUrl) return

    setDownloadingId(doc.id)
    try {
      // Fetch the file as a blob to force download instead of opening in new tab
      const response = await fetch(doc.downloadUrl)
      if (!response.ok) {
        throw new Error('Failed to fetch file')
      }

      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = doc.title || `document-${doc.id}.pdf`
      link.style.display = 'none'

      // Append to body, click, and remove
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up the blob URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl)
      }, 100)
    } catch (error) {
      console.error('Failed to download document:', error)
      // Fallback: open in new tab if download fails
      if (doc.downloadUrl) {
        window.open(doc.downloadUrl, '_blank')
      }
    } finally {
      setDownloadingId(null)
    }
  }

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'authorized':
        return 'success'
      case 'pending':
        return 'warning'
      case 'rejected':
        return 'error'
      default:
        return 'default'
    }
  }

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'authorized':
        return 'Authorized'
      case 'pending':
        return 'Authorization Pending'
      case 'rejected':
        return 'Rejected'
      default:
        return ''
    }
  }

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1)
    }
  }

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1)
    }
  }

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">Documents</Typography>
          {/* {onUpload && (
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={onUpload}
              size="small"
            >
              Upload
            </Button>
          )} */}
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        ) : documents.length === 0 ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No documents available
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Type
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Created On
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600, color: 'primary.main' }}
                    >
                      Download
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map(doc => (
                    <TableRow key={doc.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {doc.title || doc.type}
                          </Typography>
                          {doc.status && (
                            <Chip
                              label={getStatusLabel(doc.status)}
                              color={getStatusColor(doc.status)}
                              size="small"
                              sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(doc.createdOn), 'MM/dd/yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(doc)}
                          disabled={downloadingId === doc.id}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          {downloadingId === doc.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <DownloadIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            {total > 0 && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={2}
                pt={2}
                borderTop="1px solid"
                borderColor="divider"
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {documents.length} of {total} documents
                </Typography>
                {totalPages > 1 && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      size="small"
                      startIcon={<ChevronLeftIcon />}
                      onClick={handlePreviousPage}
                      disabled={page === 1 || isLoading}
                      variant="outlined"
                    >
                      Previous
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Page {page} of {totalPages}
                    </Typography>
                    <Button
                      size="small"
                      endIcon={<ChevronRightIcon />}
                      onClick={handleNextPage}
                      disabled={page >= totalPages || isLoading}
                      variant="outlined"
                    >
                      Next
                    </Button>
                  </Stack>
                )}
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default DocumentsSection
